import { computed, DestroyRef, effect, inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { Auth, getIdToken, onAuthStateChanged, onIdTokenChanged, signInAnonymously, User } from '@angular/fire/auth';
import { catchError, from, of, switchMap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { environment } from '../../../../environments/environment';
import { doc, getDoc, getFirestore } from '@angular/fire/firestore';
import { LogLevel, setLogLevel } from '@angular/fire';
import { InitAppStateService } from '../../../shared/states/init-app-state.service';
setLogLevel(LogLevel.VERBOSE);
@Injectable({
  providedIn: 'root',
})
export class FirebaseService {
  readonly TAG = 'FirebaseService';
  private auth: Auth = inject(Auth);
  private currentUser: WritableSignal<User | null> = signal(null);
  private currentUserToken: WritableSignal<string | null> = signal<string | null>(null);
  public readonly userToken: Signal<string | null> = computed(() => this.currentUserToken());
  private clientId: WritableSignal<string | null> = signal<string | null>(null);
  public readonly client_id: Signal<string | null> = computed(() => this.clientId());
  private initAppState = inject(InitAppStateService);
  private destroyRef = inject(DestroyRef);

  constructor() {
    this.auth.tenantId = environment.TENANT_ID;
    this.setupTokenEffect();
  }

  private setupTokenEffect(): void {
    effect(() => {
      const user = this.currentUser();
      if (user) {
        this.getToken(user);
      }
    });
  }

  listenForUserTokenChanges() {
    onIdTokenChanged(this.auth, async user => {
      if (user) {
        getIdToken(user, true).then(token => {
          this.currentUserToken.set(token);
        });
      } else {
        this.currentUserToken.set(null);
      }
    });
  }

  private getToken(user: User): void {
    from(user.getIdToken())
      .pipe(
        switchMap(token => of(token)),
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(token => this.currentUserToken.set(token));
  }

  async initApp() {
    await this.loginFirebase();
    await this.getFirebaseClientId();
    this.initAppState.setInitialized(true);
  }

  async loginFirebase() {
    const user = await this.isLoggedIn();
    if (!user) {
      await this.signInAnonymously();
    }
  }

  isLoggedIn(): Promise<User | null> {
    return new Promise(resolve => {
      onAuthStateChanged(
        this.auth,
        user => {
          if (user) {
            resolve(user);
          } else {
            resolve(null);
          }
          this.listenForUserTokenChanges();
        },
        () => resolve(null)
      );
    });
  }

  async signInAnonymously(): Promise<User> {
    return await signInAnonymously(this.auth).then(({ user }) => {
      return user;
    });
  }

  async getFirebaseClientId() {
    const docRef = doc(getFirestore(), 'config', 'client-id');
    const docSnap = getDoc(docRef);
    const document = await docSnap;
    const client = document.data() ?? {};
    const client_id: string = client['client-id'];
    this.clientId.set(client_id);
  }
}
